import { useMemo } from 'react';
import { Wallet } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { MobileListCard, MobileListCardStack } from '@/components/ui/MobileListCard';
import { useIsMobile } from '@/lib/useMediaQuery';
import { fmtQty } from '@/features/manufacturing/planning/engine';
import type { PlanningModel } from '@/features/manufacturing/planning/types';
import { formatCurrency } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { InlineStat, monthLabel, shortDate } from './shared';

interface CashTabProps {
  model: PlanningModel;
  onOpenSettings: () => void;
}

/**
 * What the plan costs, month by month: suggested orders (by the month
 * they should be placed), draft POs still to place, and placed POs
 * still to arrive — against the purchasing budget when one is set.
 */
export function CashTab({ model, onOpenSettings }: CashTabProps) {
  const isMobile = useIsMobile();
  const { cash } = model;
  const budget = model.settings.monthlyPurchasingBudgetCents;
  const maxBar = useMemo(
    () =>
      Math.max(
        1,
        ...cash.buckets.map((b) =>
          Math.max(b.plannedCents + b.draftCents, b.committedCents, b.budgetCents ?? 0),
        ),
      ),
    [cash.buckets],
  );
  const ordersById = useMemo(() => new Map(model.orders.map((o) => [o.id, o])), [model.orders]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        <InlineStat
          label={`Suggested orders (${model.settings.horizonDays}d)`}
          value={formatCurrency(cash.plannedCents)}
        />
        <InlineStat label="Draft POs to place" value={formatCurrency(cash.draftCents)} />
        <InlineStat label="Placed, still arriving" value={formatCurrency(cash.committedCents)} />
        <InlineStat
          label="Material the plan consumes"
          value={formatCurrency(cash.planMaterialCents)}
        />
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Wallet className="h-4 w-4 text-info" />
                Purchasing cash by month
              </CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">
                {budget != null
                  ? `Budget ${formatCurrency(budget)} / month. ${
                      cash.overBudgetMonths > 0
                        ? `${cash.overBudgetMonths} month${cash.overBudgetMonths === 1 ? '' : 's'} over — push targets out, split orders, or raise the cap.`
                        : 'Every month fits.'
                    }`
                  : 'No monthly budget set — add one in settings to see where the plan outruns cash.'}
              </p>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={onOpenSettings}>
              {budget != null ? 'Change budget' : 'Set a budget'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            {cash.buckets.map((bucket) => {
              const outflow = bucket.plannedCents + bucket.draftCents;
              const over = bucket.overBudgetCents > 0;
              return (
                <div key={bucket.key} className="space-y-1">
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="font-medium">{monthLabel(bucket.key)}</span>
                    <span
                      className={cn(
                        'tabular-nums',
                        over ? 'text-fresh-salmon-dark' : 'text-muted-foreground',
                      )}
                    >
                      {formatCurrency(outflow)} to place
                      {over && ` · ${formatCurrency(bucket.overBudgetCents)} over`}
                    </span>
                  </div>
                  <div className="relative h-3 overflow-hidden rounded-full bg-secondary">
                    <div
                      className={cn(
                        'absolute inset-y-0 left-0 rounded-full',
                        over ? 'bg-fresh-salmon-dark' : 'bg-info',
                      )}
                      style={{ width: `${Math.min(100, (bucket.plannedCents / maxBar) * 100)}%` }}
                      title={`Suggested orders ${formatCurrency(bucket.plannedCents)}`}
                    />
                    <div
                      className={cn(
                        'absolute inset-y-0 rounded-r-full',
                        over ? 'bg-fresh-salmon' : 'bg-info-soft',
                      )}
                      style={{
                        left: `${Math.min(100, (bucket.plannedCents / maxBar) * 100)}%`,
                        width: `${Math.min(100, (bucket.draftCents / maxBar) * 100)}%`,
                      }}
                      title={`Draft POs ${formatCurrency(bucket.draftCents)}`}
                    />
                    {bucket.budgetCents != null && (
                      <div
                        className="absolute inset-y-0 w-0.5 bg-humus-dark"
                        style={{ left: `${Math.min(100, (bucket.budgetCents / maxBar) * 100)}%` }}
                        title={`Budget ${formatCurrency(bucket.budgetCents)}`}
                      />
                    )}
                  </div>
                </div>
              );
            })}
            <div className="flex flex-wrap gap-4 text-[11px] text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2 w-3 rounded-sm bg-info" /> suggested orders
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2 w-3 rounded-sm bg-info-soft" /> draft POs
              </span>
              {budget != null && (
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2 w-0.5 bg-humus-dark" /> budget
                </span>
              )}
            </div>
          </div>

          {isMobile ? (
            <MobileListCardStack>
              {cash.buckets.map((bucket) => (
                <MobileListCard
                  key={bucket.key}
                  title={monthLabel(bucket.key)}
                  subtitle={
                    budget != null ? `Budget ${formatCurrency(bucket.budgetCents ?? 0)}` : undefined
                  }
                  amount={{
                    value: formatCurrency(bucket.plannedCents + bucket.draftCents),
                    caption: 'to place',
                    tone: bucket.overBudgetCents > 0 ? 'negative' : 'neutral',
                  }}
                  badges={
                    bucket.overBudgetCents > 0 ? (
                      <Badge variant="warning-soft">
                        Over by {formatCurrency(bucket.overBudgetCents)}
                      </Badge>
                    ) : undefined
                  }
                  meta={[
                    { label: 'Suggested', value: formatCurrency(bucket.plannedCents) },
                    { label: 'Draft POs', value: formatCurrency(bucket.draftCents) },
                    { label: 'Arriving', value: formatCurrency(bucket.committedCents) },
                  ]}
                  trailing={null}
                />
              ))}
            </MobileListCardStack>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Suggested orders</TableHead>
                  <TableHead className="text-right">Draft POs</TableHead>
                  <TableHead className="text-right">Arriving (committed)</TableHead>
                  {budget != null && <TableHead className="text-right">Budget</TableHead>}
                  {budget != null && <TableHead className="text-right">Over by</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {cash.buckets.map((bucket) => (
                  <TableRow key={bucket.key}>
                    <TableCell className="font-medium">
                      {monthLabel(bucket.key)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(bucket.plannedCents)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(bucket.draftCents)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {formatCurrency(bucket.committedCents)}
                    </TableCell>
                    {budget != null && (
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(bucket.budgetCents ?? 0)}
                      </TableCell>
                    )}
                    {budget != null && (
                      <TableCell
                        className={cn(
                          'text-right tabular-nums',
                          bucket.overBudgetCents > 0 && 'font-medium text-fresh-salmon-dark',
                        )}
                      >
                        {bucket.overBudgetCents > 0 ? formatCurrency(bucket.overBudgetCents) : '—'}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                <TableRow className="font-medium">
                  <TableCell>Total</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCurrency(cash.plannedCents)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCurrency(cash.draftCents)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {formatCurrency(cash.committedCents)}
                  </TableCell>
                  {budget != null && <TableCell />}
                  {budget != null && <TableCell />}
                </TableRow>
              </TableBody>
            </Table>
          )}

          {cash.ordersWithoutCost > 0 && (
            <p className="text-xs text-muted-foreground">
              {cash.ordersWithoutCost} suggested order
              {cash.ordersWithoutCost === 1 ? ' has' : 's have'} no unit cost on file and{' '}
              {cash.ordersWithoutCost === 1 ? 'is' : 'are'} not counted above — see Missing info.
            </p>
          )}
        </CardContent>
      </Card>

      {cash.overBudgetMonths > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Orders in over-budget months</CardTitle>
            <p className="text-sm text-muted-foreground">
              Largest first. Delaying a target, splitting an order to its MOQ, or skipping a price
              break are the usual levers.
            </p>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-border">
              {cash.buckets
                .filter((b) => b.overBudgetCents > 0)
                .flatMap((b) =>
                  b.orderIds
                    .map((id) => ordersById.get(id))
                    .filter((o): o is NonNullable<typeof o> => !!o && o.totalCents != null)
                    .sort((a, c) => (c.totalCents ?? 0) - (a.totalCents ?? 0))
                    .slice(0, 8)
                    .map((order) => ({ bucket: b, order })),
                )
                .map(({ bucket, order }) => {
                  const plan = model.itemsById.get(order.itemId);
                  return (
                    <li
                      key={order.id}
                      className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm"
                    >
                      <div className="min-w-0">
                        <span className="font-medium">{plan?.item.name ?? 'Part'}</span>
                        <span className="ml-2 text-xs text-muted-foreground">
                          {fmtQty(order.quantity)} · order by {shortDate(order.idealOrderDate)} ·{' '}
                          {order.supplier?.vendorName ?? 'no vendor'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{monthLabel(bucket.key)}</Badge>
                        <span className="tabular-nums">
                          {formatCurrency(order.totalCents ?? 0)}
                        </span>
                      </div>
                    </li>
                  );
                })}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
